import React from 'react';

/**
 * AdminErrorBoundary component for handling errors in admin interface
 * Provides admin-specific error handling and recovery options
 */
class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for admin monitoring
    console.error('Admin Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // In a real app, you might want to log this to an error reporting service
    // logErrorToService(error, errorInfo, 'admin-interface');
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleGoToDashboard = () => {
    window.location.href = '/admin';
  };

  render() {
    if (this.state.hasError) {
      const { fallbackMessage } = this.props;
      
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="text-center">
              <div className="text-red-400 text-6xl mb-4">⚠️</div>
              <h1 className="text-2xl font-bold text-white mb-4 dark:text-white">
                Admin Interface Error
              </h1>
              <p className="text-gray-300 mb-6 dark:text-gray-600">
                {fallbackMessage || 'An unexpected error occurred in the admin interface. This has been logged for review.'}
              </p>
              
              {/* Error details for debugging (only in development) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mb-6 text-left">
                  <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300 mb-2 dark:text-gray-600">
                    Error Details (Development Only)
                  </summary>
                  <div className="bg-gray-900 p-3 rounded text-xs text-red-300 overflow-auto max-h-32">
                    <div className="font-semibold mb-2">Error:</div>
                    <div className="mb-2">{this.state.error.toString()}</div>
                    <div className="font-semibold mb-2">Stack Trace:</div>
                    <pre className="whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleRetry}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors dark:text-white"
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleGoBack}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors dark:text-white"
                >
                  Go Back
                </button>
                <button
                  onClick={this.handleGoToDashboard}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors dark:text-white"
                >
                  Admin Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AdminErrorBoundary;
